import React, { useEffect, useRef, useState } from 'react';
import { createRoom, joinRoom, connectToRoom } from './net';
import CityBackdrop from './CityBackdrop';

/** How long the copy button stays in its confirmed state, in ms. */
const COPIED_FEEDBACK_MS = 1600;

/**
 * Build the invite link for a room.
 *
 * Deliberately derived from the page's own location rather than a baked-in
 * production URL: the link then works unchanged from a preview deployment, a
 * LAN address during testing, or localhost.
 *
 * @param {string} code Room code.
 * @returns {string} absolute URL that opens the game on this room's join step.
 */
function inviteLink(code) {
  const { origin, pathname } = window.location;
  return `${origin}${pathname}?room=${encodeURIComponent(code)}`;
}

/**
 * Lobby — online matchmaking flow for Sky-Drop.
 *
 * The host creates a room and shares its short code; the guest joins by code.
 * Each side opens a WebSocket and signals readiness once both players are
 * present. When the backend broadcasts `game_start`, the live connection and
 * this client's assigned player slot are handed back to App via onGameStart,
 * which begins the match.
 *
 * Ready handshake (backend starts the game once both players are ready):
 *   - Guest sends `player_ready` on connect — its presence means the room is full.
 *   - Host sends `player_ready` when it receives the opponent's `player_connected`.
 *
 * A room can also be shared as a link (`?room=CODE`). App reads that parameter
 * and hands it back here as `initialRoom`, which opens the lobby straight on
 * the join step with the code already filled in — the invited player never has
 * to transcribe anything.
 *
 * @param {object} props
 * @param {string} [props.initialRoom] Room code from an invite link.
 * @param {() => void} props.onCancel   Return to the main menu.
 * @param {(session: {connection: object, roomId: string, playerId: string,
 *                     playerSlot: 'player1'|'player2'}) => void} props.onGameStart
 */
const Lobby = ({ onCancel, onGameStart, initialRoom = '' }) => {
  // An invite link lands on the join step; everyone else starts at the fork.
  const [view, setView] = useState(initialRoom ? 'join' : 'choose');
  const [playerName, setPlayerName] = useState('');
  const [joinCode, setJoinCode] = useState(initialRoom);
  const [roomCode, setRoomCode] = useState(null); // host's shareable code
  const [status, setStatus] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);

  const connRef = useRef(null);
  const linkRef = useRef(null);
  const copyTimerRef = useRef(null);
  const startedRef = useRef(false); // true once game_start fired (don't close socket)

  // If the lobby unmounts before a match starts (e.g. user cancels), tear down
  // any open socket so we don't leak connections or leave phantom players.
  useEffect(() => {
    return () => {
      if (!startedRef.current && connRef.current) {
        connRef.current.close();
      }
      clearTimeout(copyTimerRef.current);
    };
  }, []);

  /**
   * Put the invite link on the clipboard.
   *
   * The async Clipboard API is only available in a secure context, so this
   * falls back to selecting the field and running the legacy copy command —
   * otherwise the button silently does nothing when the game is served over
   * plain http on a LAN, which is exactly when people are sharing a link.
   */
  const copyInvite = async () => {
    const link = inviteLink(roomCode);
    try {
      await navigator.clipboard.writeText(link);
    } catch (err) {
      const field = linkRef.current;
      if (!field) return;
      field.select();
      document.execCommand('copy');
    }
    setCopied(true);
    clearTimeout(copyTimerRef.current);
    copyTimerRef.current = setTimeout(() => setCopied(false), COPIED_FEEDBACK_MS);
  };

  /**
   * Open the room WebSocket and wire the ready/start handshake for this slot.
   * @param {string} roomId
   * @param {string} playerId
   * @param {'player1'|'player2'} playerSlot
   */
  const openConnection = (roomId, playerId, playerSlot) => {
    const conn = connectToRoom(roomId, playerId, {
      onOpen: () => {
        // Guest joining a room implies it is now full — ready up immediately.
        if (playerSlot === 'player2') {
          conn.send({ type: 'player_ready' });
          setStatus('Joined! Waiting for host…');
        }
      },
      onMessage: (msg) => {
        if (msg.type === 'player_connected' && msg.playerId !== playerId) {
          // Host sees the opponent arrive — ready up to trigger the start.
          if (playerSlot === 'player1') {
            conn.send({ type: 'player_ready' });
            setStatus('Opponent connected! Starting…');
          }
        } else if (msg.type === 'game_start') {
          startedRef.current = true;
          onGameStart({ connection: conn, roomId, playerId, playerSlot });
        } else if (msg.type === 'player_disconnected' && msg.playerId !== playerId) {
          setStatus('Opponent disconnected. Waiting…');
        }
      },
      onError: () => setError('Connection error — is the backend running?'),
      onClose: () => {
        if (!startedRef.current) setStatus('Disconnected.');
      },
    });
    connRef.current = conn;
  };

  /** Create a new room as the host (player1). */
  const handleCreate = async () => {
    setError('');
    setBusy(true);
    try {
      const { roomId, playerId } = await createRoom(playerName || 'Host', 'online');
      setRoomCode(roomId);
      setStatus('Waiting for an opponent to join…');
      openConnection(roomId, playerId, 'player1');
    } catch (e) {
      setError(e.message || 'Failed to create room.');
    } finally {
      setBusy(false);
    }
  };

  /** Join an existing room as the guest (player2). */
  const handleJoin = async () => {
    setError('');
    setBusy(true);
    try {
      const code = joinCode.trim().toLowerCase();
      const { roomId, playerId } = await joinRoom(code, playerName || 'Guest');
      setStatus('Connecting…');
      openConnection(roomId, playerId, 'player2');
    } catch (e) {
      setError(e.message || 'Failed to join room.');
    } finally {
      setBusy(false);
    }
  };

  // --- Render ---------------------------------------------------------------

  return (
    <div className="game-container">
      {/* The arena's own city, drifting behind the matchmaking — the lobby
          is the rooftop you wait on before the fight. */}
      <CityBackdrop />
      <div className="menu lobby">
        <h1>Online Match</h1>
        <p className="lobby-sub">
          {view === 'choose' && 'Host a room and share the code, or join a friend’s.'}
          {view === 'create' && (roomCode ? 'Room open — waiting on the rooftop.' : 'Open a room for a friend to join.')}
          {view === 'join' && (initialRoom ? 'You have been called to a fight.' : 'Enter the code your friend shared.')}
        </p>

        {/* Choose: create or join */}
        {view === 'choose' && (
          <>
            <input
              className="lobby-input"
              type="text"
              placeholder="Your name"
              maxLength={16}
              value={playerName}
              onChange={(e) => setPlayerName(e.target.value)}
            />
            <div className="lobby-actions">
              <button className="start-btn" onClick={() => setView('create')}>
                Create Room
              </button>
              <button className="start-btn" onClick={() => setView('join')}>
                Join Room
              </button>
            </div>
            <button className="lobby-back" onClick={onCancel}>
              ← Back to Menu
            </button>
          </>
        )}

        {/* Create: show code and wait */}
        {view === 'create' && (
          <>
            {!roomCode ? (
              <button className="start-btn" disabled={busy} onClick={handleCreate}>
                {busy ? 'Creating…' : 'Create Room'}
              </button>
            ) : (
              <div className="lobby-code-box">
                <span className="lobby-code-label">Room Code</span>
                <span className="lobby-code">{roomCode}</span>
                <span className="lobby-hint">
                  Read out the code, or send the link — it drops them straight
                  into this room.
                </span>
                <div className="lobby-share">
                  <input
                    ref={linkRef}
                    className="lobby-link"
                    type="text"
                    readOnly
                    value={inviteLink(roomCode)}
                    // Selecting on focus means the link is still shareable by
                    // hand if the clipboard is unavailable or blocked.
                    onFocus={(e) => e.target.select()}
                  />
                  <button
                    className="lobby-copy"
                    onClick={copyInvite}
                    aria-label="Copy invite link"
                  >
                    {copied ? 'Copied' : 'Copy'}
                  </button>
                </div>
              </div>
            )}
            {status && <p className="lobby-status">{status}</p>}
            {error && <p className="lobby-error">{error}</p>}
            <button className="lobby-back" onClick={onCancel}>
              ← Cancel
            </button>
          </>
        )}

        {/* Join: enter code, or confirm the one an invite link supplied */}
        {view === 'join' && (
          <>
            {initialRoom && (
              <>
                {/* Arriving by link skips the fork, and with it the only place
                    a name could be entered — so it is offered here instead. */}
                <p className="lobby-invited">
                  Invited to room <strong>{initialRoom}</strong>
                </p>
                <input
                  className="lobby-input"
                  type="text"
                  placeholder="Your name"
                  maxLength={16}
                  autoFocus
                  value={playerName}
                  onChange={(e) => setPlayerName(e.target.value)}
                />
              </>
            )}
            {!initialRoom && (
              <input
                className="lobby-input"
                type="text"
                placeholder="Room code"
                maxLength={8}
                value={joinCode}
                onChange={(e) => setJoinCode(e.target.value)}
              />
            )}
            <button
              className="start-btn"
              disabled={busy || !joinCode.trim()}
              onClick={handleJoin}
            >
              {busy ? 'Joining…' : 'Join Room'}
            </button>
            {status && <p className="lobby-status">{status}</p>}
            {error && <p className="lobby-error">{error}</p>}
            <button className="lobby-back" onClick={onCancel}>
              ← Cancel
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default Lobby;
